const CONSENT_KEY = 'isbd_consent';
const VISITOR_KEY = 'isbd_vid';
const SESSION_KEY = 'isbd_sid';
const SESSION_TOUCH_KEY = 'isbd_sid_touch';
const UTM_KEY = 'isbd_utm';

const SESSION_TTL_MS = 30 * 60 * 1000; // 30 min d'inactivité
const VISITOR_MAX_AGE_DAYS = 365;
const CONSENT_MAX_AGE_DAYS = 365;

export type ConsentChoice = 'accepted' | 'rejected' | null;

export type StoredUtm = {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
};

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeDays: number) {
  if (typeof document === 'undefined') return;
  const maxAge = Math.floor(maxAgeDays * 24 * 60 * 60);
  const secure = typeof location !== 'undefined' && location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}`;
}

function clearCookie(name: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; Path=/; Max-Age=0; SameSite=Lax`;
}

function uuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function getConsent(): ConsentChoice {
  const raw = readCookie(CONSENT_KEY);
  if (raw === 'accepted' || raw === 'rejected') return raw;
  try {
    const ls = localStorage.getItem(CONSENT_KEY);
    if (ls === 'accepted' || ls === 'rejected') return ls;
  } catch {
    /* ignore */
  }
  return null;
}

export function setConsent(choice: 'accepted' | 'rejected') {
  writeCookie(CONSENT_KEY, choice, CONSENT_MAX_AGE_DAYS);
  try {
    localStorage.setItem(CONSENT_KEY, choice);
  } catch {
    /* ignore */
  }
  if (choice === 'rejected') {
    clearCookie(VISITOR_KEY);
    clearCookie(SESSION_KEY);
    clearCookie(SESSION_TOUCH_KEY);
    clearCookie(UTM_KEY);
    try {
      localStorage.removeItem(VISITOR_KEY);
      sessionStorage.removeItem(SESSION_KEY);
      sessionStorage.removeItem(SESSION_TOUCH_KEY);
      sessionStorage.removeItem(UTM_KEY);
    } catch {
      /* ignore */
    }
  }
}

export function getOrCreateVisitorId(): string | null {
  if (getConsent() !== 'accepted') return null;
  let id = readCookie(VISITOR_KEY);
  if (!id) {
    try {
      id = localStorage.getItem(VISITOR_KEY);
    } catch {
      id = null;
    }
  }
  if (!id) {
    id = uuid();
  }
  writeCookie(VISITOR_KEY, id, VISITOR_MAX_AGE_DAYS);
  try {
    localStorage.setItem(VISITOR_KEY, id);
  } catch {
    /* ignore */
  }
  return id;
}

export function getOrCreateSessionId(): string | null {
  if (getConsent() !== 'accepted') return null;
  const now = Date.now();
  let sid: string | null = null;
  let touch = 0;
  try {
    sid = sessionStorage.getItem(SESSION_KEY) || readCookie(SESSION_KEY);
    touch = Number(sessionStorage.getItem(SESSION_TOUCH_KEY) || readCookie(SESSION_TOUCH_KEY) || 0);
  } catch {
    sid = readCookie(SESSION_KEY);
    touch = Number(readCookie(SESSION_TOUCH_KEY) || 0);
  }

  if (!sid || !touch || now - touch > SESSION_TTL_MS) {
    sid = uuid();
  }

  writeCookie(SESSION_KEY, sid, 1);
  writeCookie(SESSION_TOUCH_KEY, String(now), 1);
  try {
    sessionStorage.setItem(SESSION_KEY, sid);
    sessionStorage.setItem(SESSION_TOUCH_KEY, String(now));
  } catch {
    /* ignore */
  }
  return sid;
}

export function touchSession() {
  if (getConsent() !== 'accepted') return;
  const sid = getOrCreateSessionId();
  if (!sid) return;
  const now = Date.now();
  writeCookie(SESSION_TOUCH_KEY, String(now), 1);
  try {
    sessionStorage.setItem(SESSION_TOUCH_KEY, String(now));
  } catch {
    /* ignore */
  }
}

export function captureUtmFromLocation(search: string): StoredUtm | null {
  if (getConsent() !== 'accepted') return null;
  const params = new URLSearchParams(search);
  const utm: StoredUtm = {};
  const map: Array<keyof StoredUtm> = [
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
    'utm_term',
  ];
  let found = false;
  for (const key of map) {
    const v = params.get(key);
    if (v) {
      utm[key] = v.slice(0, 180);
      found = true;
    }
  }
  if (!found) {
    try {
      const raw = sessionStorage.getItem(UTM_KEY) || readCookie(UTM_KEY);
      if (raw) return JSON.parse(raw) as StoredUtm;
    } catch {
      return null;
    }
    return null;
  }
  const serialized = JSON.stringify(utm);
  writeCookie(UTM_KEY, serialized, 1);
  try {
    sessionStorage.setItem(UTM_KEY, serialized);
  } catch {
    /* ignore */
  }
  return utm;
}

export function getStoredUtm(): StoredUtm {
  try {
    const raw = sessionStorage.getItem(UTM_KEY) || readCookie(UTM_KEY);
    if (raw) return JSON.parse(raw) as StoredUtm;
  } catch {
    /* ignore */
  }
  return {};
}

export function detectDeviceType(): 'mobile' | 'tablet' | 'desktop' | 'unknown' {
  if (typeof navigator === 'undefined') return 'unknown';
  const ua = navigator.userAgent || '';
  if (/iPad|Tablet|Android(?!.*Mobile)/i.test(ua)) return 'tablet';
  if (/Mobi|Android|iPhone|iPod/i.test(ua)) return 'mobile';
  if (ua) return 'desktop';
  return 'unknown';
}
